import { useState, useRef } from 'react'
import { useAppStore } from '@/store'
import RegionSelectionModal from '@/routes/DataCatalog/RegionSelectionModal'
import CallConfigModal from './CallConfigModal'
import { UnifiedUploadModal } from '@/components/UnifiedFileUpload'
import { apiService } from '@/service/api'

import { MODAL_TYPE } from '@/store/modalSlice'
import type { BoundingBox } from '@/store/dataCatalogSlice'

interface FileWithRegions {
  file: File
  bboxes: BoundingBox[] | null
  name: string
  description: string
  type: 'pnid' | 'pfd'
  autoRename?: boolean
  replaceDocumentId?: string
}

const ModalProvider = () => {
  const { currentOpenedModal, modalData, closeModal, onModalSubmit, setModalData , openModal, fetchDocuments, uploadCallbacks } = useAppStore()
  // P&ID/PFD queue state - persists across modal switches
  const [pnidQueue, setPnidQueue] = useState<File[]>([])
  const [currentPnidFileType, setCurrentPnidFileType] = useState<'pnid' | 'pfd'>('pnid')

  // Per-file duplicate options for PFD/P&ID queue (fileName → options)
  const [pnidDuplicateOptions, setPnidDuplicateOptions] = useState<
    Map<string, { autoRename?: boolean; replaceDocumentId?: string }>
  >(new Map())

  // Tracks whether the current upload flow has multiple files
  const [isBatchUpload, setIsBatchUpload] = useState(false)

  // Multi-page mode: collect region selections before uploading
  const [isMultiPageMode, setIsMultiPageMode] = useState(false)
  const [collectedFiles, setCollectedFiles] = useState<FileWithRegions[]>([])

  // Track active multi-page batch ID for cancellation cleanup
  const activeBatchIdRef = useRef<string | null>(null)

  // FS-499: pending-batch state for the region-selection flow. Populated
  // when a multi-file batch starts via /register-pending; consumed by
  // onModalSubmit (claims each row via docId), onSkip (discards individual
  // rows), and onClose (discards any leftover queue). Cleared at batch end.
  const pendingBatchRef = useRef<{ batchId: string; docIdByName: Map<string, string> } | null>(null)

  // Compute open state for each modal.
  // All modals are rendered simultaneously so that Radix Dialog always
  // receives a proper open→closed transition instead of being unmounted
  // while still open (which leaves pointer-events:none stuck on <body>).
  const isUploadOpen = currentOpenedModal === MODAL_TYPE.uploadFile
  const isRegionSelectionOpen = currentOpenedModal === MODAL_TYPE.regionSelection
  const isCallConfigOpen = currentOpenedModal === MODAL_TYPE.callConfig

  const uploadModalData = modalData?.[MODAL_TYPE.uploadFile] as {
    initialFiles?: File[]
  } | undefined

  const regionDoc = modalData?.[MODAL_TYPE.regionSelection] ?? null
  const isPfd = regionDoc?.type === 'pfd'

  const uploadMultiPageFiles = async (filesToUpload: FileWithRegions[]) => {
    const store = useAppStore.getState()
    const uploadDocument = store.uploadDocument
    const uploadCallbacks = store.uploadCallbacks
    const fetchDocuments = store.fetchDocuments

    // Generate a unique set_id for this multi-page set (fallback for older browsers e.g. Safari < 15.4)
    const setId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

    const multiPageSetName = filesToUpload[0].name.replace(/\s*\(\d+\)\s*/, '').replace(/\.[^.]+$/, '')
    const sheetCount = String(filesToUpload.length)

    // Track batch ID so cancellation can clean up already-uploaded files
    activeBatchIdRef.current = setId

    // Pre-register ALL files upfront so the multi-page folder appears immediately
    // in the data catalog with all pages inside it showing "uploading".
    const filePlan = filesToUpload.map((fileWithRegions, index) => {
      const fileName = fileWithRegions.file.name
      const cleanedBboxes = fileWithRegions.bboxes?.map((bbox: BoundingBox) => {
        const { x, y, width, height, type, page } = bbox
        return { x, y, width, height, type, ...(page != null && { page }) }
      })

      const multiPageProps = {
        multi_page_set_id: setId,
        sheet_index: String(index),
        sheet_count: sheetCount,
        multi_page_set_name: multiPageSetName,
      }

      // Register with multi-page properties so temp doc is grouped into a folder
      let uploadStartedAt: string | undefined
      if (uploadCallbacks?.onStart && fileName) {
        const result = uploadCallbacks.onStart(fileName, fileWithRegions.type, { fileSize: fileWithRegions.file.size, multiPageProps })
        if (typeof result === 'string') uploadStartedAt = result
      }

      const signal = uploadCallbacks?.getAbortSignal?.(fileName)

      return { fileWithRegions, cleanedBboxes, multiPageProps, uploadStartedAt, signal }
    })

    // Upload sequentially — all files are already visible in the folder
    for (const { fileWithRegions, cleanedBboxes, multiPageProps, uploadStartedAt, signal } of filePlan) {
      const fileName = fileWithRegions.file.name

      if (signal?.aborted) {
        uploadCallbacks?.onComplete?.(fileName)
        continue
      }

      try {
        await uploadDocument(fileWithRegions.file, {
          name: fileWithRegions.name,
          description: fileWithRegions.description,
          type: fileWithRegions.type,
          ...(cleanedBboxes && { bboxes: cleanedBboxes }),
          properties: multiPageProps,
        }, {
          signal,
          skipToast: true,
          autoRename: fileWithRegions.autoRename,
          replaceDocumentId: fileWithRegions.replaceDocumentId,
          uploadStartedAt,
        })
      } catch (err) {
        console.error(`Multi-page upload failed for ${fileName}:`, err)
      } finally {
        if (uploadCallbacks?.onComplete && fileName) {
          uploadCallbacks.onComplete(fileName)
        }
      }
    }

    // Batch upload complete — clear the ref so onClose won't cancel
    activeBatchIdRef.current = null

    // Refresh documents and clean up state — always runs even if some uploads failed
    await fetchDocuments()
    setCollectedFiles([])
    setIsMultiPageMode(false)
    setIsBatchUpload(false)
    setPnidQueue([])
    setPnidDuplicateOptions(new Map())
  }

  return (
    <>
      {/* Upload modal */}
      <UnifiedUploadModal
            isOpen={isUploadOpen}
            onClose={() => {
              setPnidQueue([])  // Clear queue when explicitly closing
              setPnidDuplicateOptions(new Map())
              // Clear initialFiles so they don't persist to next open
              setModalData(MODAL_TYPE.uploadFile, { initialFiles: undefined })
              closeModal()
            }}
            initialFiles={uploadModalData?.initialFiles}
            uploadCallbacks={uploadCallbacks}
            onUploadComplete={(fileId, fileName) => {
              console.log(`File uploaded: ${fileName} (${fileId})`)
              // Refresh document list so the new file appears in the data catalog
              fetchDocuments()
            }}
            onPnidPfdUpload={async (files, fileType, duplicateOptions) => {
              // For P&ID/PFD, store remaining files in queue and open region selection for first
              const fileArray = Array.isArray(files) ? files : [files]

              // Bypass area selection: upload P&ID/PFD files directly without region selection
              if (duplicateOptions?.bypassAreaSelection) {
                // Close modal immediately — upload progress shows on the data catalog page
                closeModal()

                const isMultiPage = duplicateOptions?.isMultiPage === true && fileArray.length > 1

                // For single-PDF bypass, detect page count so backend can fan out per-page jobs.
                // Without this, a multi-page PDF would only process page 1.
                let singlePdfPageCount: number | undefined
                if (fileArray.length === 1 && fileArray[0].type === 'application/pdf') {
                  try {
                    const { getDocument } = await import('pdfjs-dist')
                    const arrayBuffer = await fileArray[0].arrayBuffer()
                    const pdf = await getDocument({ data: arrayBuffer }).promise
                    if (pdf.numPages > 1) {
                      singlePdfPageCount = pdf.numPages
                    }
                    pdf.destroy()
                  } catch {
                    // If page detection fails, upload normally (single-job, processes page 1)
                  }
                }

                // Generate multi-page set properties when multi-page is checked
                const setId = isMultiPage
                  ? (typeof crypto !== 'undefined' && 'randomUUID' in crypto
                      ? crypto.randomUUID()
                      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`)
                  : undefined
                const multiPageSetName = isMultiPage
                  ? fileArray[0].name.replace(/\s*\(\d+\)\s*/, '').replace(/\.[^.]+$/, '')
                  : undefined
                const sheetCount = isMultiPage ? String(fileArray.length) : undefined

                // FS-499: pre-register every file in the batch server-side
                // BEFORE local temp docs are created. The returned docIds back
                // the local rows, so a page refresh mid-upload leaves the
                // pending rows in Postgres (catalog can show them on reload)
                // instead of silently dropping the queue. Falls back to the
                // legacy local-only path on API failure so a transient blip
                // doesn't block the upload entirely.
                const bypassBatchId =
                  typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
                const docIdByName: Map<string, string> = new Map()
                try {
                  const resp = await apiService.registerPendingFiles(
                    bypassBatchId,
                    fileArray.map(f => {
                      const replaceId = duplicateOptions?.duplicateMap?.get(f.name)
                      return {
                        filename: f.name,
                        fileType,
                        fileSize: f.size,
                        // If the user picked "Replace" for this file,
                        // pass the existing docId — backend soft-deletes
                        // it then registers the pending row with the
                        // original name. Otherwise auto-rename so
                        // `foo.pdf` becomes `foo (1).pdf` instead of
                        // colliding on content_hash at claim time.
                        ...(replaceId
                          ? { replaceDocumentId: replaceId }
                          : { autoRename: duplicateOptions?.autoRename ?? true }),
                      }
                    }),
                  )
                  // Map original (sent) filename → server's docId via
                  // positional index. The server may have renamed
                  // (`foo.pdf` → `foo (1).pdf`) so resp.files[i].filename
                  // is the RESOLVED name; we still key by what the user
                  // sees in the catalog row (sent name) so subsequent
                  // lookups in the upload loop succeed.
                  resp.files.forEach((r, i) => {
                    const sent = fileArray[i]?.name
                    if (sent) docIdByName.set(sent, r.docId)
                  })
                } catch (err) {
                  console.warn('[FS-499] register-pending failed; falling back to legacy local-only flow', err)
                }

                // Pre-compute per-file options and register ALL files upfront so every
                // file appears immediately in the data catalog as "uploading".  Without
                // this, temp docs are created one-at-a-time inside the sequential upload
                // loop, making later files invisible and appearing "skipped".
                const filePlan = fileArray.map((file, index) => {
                  const fileOpts: { autoRename?: boolean; replaceDocumentId?: string } = {}
                  if (duplicateOptions?.autoRename) {
                    fileOpts.autoRename = true
                  }
                  if (duplicateOptions?.duplicateMap?.has(file.name)) {
                    fileOpts.replaceDocumentId = duplicateOptions.duplicateMap.get(file.name)
                    fileOpts.autoRename = false
                  }

                  const multiPageProps: Record<string, string> = {}
                  if (isMultiPage) {
                    multiPageProps.multi_page_set_id = setId!
                    multiPageProps.sheet_index = String(index)
                    multiPageProps.sheet_count = sheetCount!
                    multiPageProps.multi_page_set_name = multiPageSetName!
                  }
                  // Pass total_pdf_pages for single multi-page PDFs so backend fans out per-page jobs
                  if (singlePdfPageCount) {
                    multiPageProps.total_pdf_pages = String(singlePdfPageCount)
                  }

                  const hasProps = Object.keys(multiPageProps).length > 0
                  const docId = docIdByName.get(file.name)

                  const uploadStartedAt = uploadCallbacks?.onStart?.(file.name, fileType, { fileSize: file.size, multiPageProps: hasProps ? multiPageProps as { multi_page_set_id: string; sheet_index: string; sheet_count: string; multi_page_set_name: string } : undefined, docId })
                  // Capture signal immediately after registration so it stays
                  // valid even if the user cancels (removes the controller) later.
                  const signal = uploadCallbacks?.getAbortSignal?.(file.name)
                  return { file, fileOpts, multiPageProps: hasProps ? multiPageProps : undefined, uploadStartedAt, signal, docId }
                })

                // Upload sequentially — each file is already visible in the UI.
                // Collect docIds for files the user aborted before their turn so
                // the server-side pending row gets cleaned up; without this
                // those rows would sit as zombies until the BullMQ sweep runs.
                const abortedDocIds: string[] = []
                for (const { file, fileOpts, multiPageProps, uploadStartedAt, signal, docId } of filePlan) {
                  // If the user cancelled this file before its turn, skip it
                  if (signal?.aborted) {
                    uploadCallbacks?.onComplete?.(file.name)
                    if (docId) abortedDocIds.push(docId)
                    continue
                  }
                  try {
                    const store = useAppStore.getState()
                    await store.uploadDocument(file, {
                      name: file.name,
                      description: '',
                      type: fileType,
                      ...(multiPageProps && { properties: multiPageProps }),
                    }, {
                      autoRename: fileOpts.autoRename,
                      replaceDocumentId: fileOpts.replaceDocumentId,
                      skipToast: true,
                      signal,
                      uploadStartedAt: typeof uploadStartedAt === 'string' ? uploadStartedAt : undefined,
                      docId,
                    })
                  } catch (err) {
                    console.error(`Bypass upload failed for ${file.name}:`, err)
                  } finally {
                    uploadCallbacks?.onComplete?.(file.name)
                  }
                }
                if (abortedDocIds.length > 0) {
                  apiService
                    .discardPendingFiles(bypassBatchId, abortedDocIds)
                    .catch(err => console.error('[FS-499] discard-pending (bypass abort) failed', err))
                }
                fetchDocuments()
                return
              }

              // Check if this is multi-page mode (multiple separate files grouped into a set)
              const isMultiPage = duplicateOptions?.isMultiPage === true
              setIsMultiPageMode(isMultiPage)

              // Build per-file options map from duplicate resolution
              const optionsMap = new Map<string, { autoRename?: boolean; replaceDocumentId?: string }>()
              if (duplicateOptions?.autoRename) {
                fileArray.forEach(f => optionsMap.set(f.name, { autoRename: true }))
              }
              if (duplicateOptions?.duplicateMap) {
                duplicateOptions.duplicateMap.forEach((existingId, fileName) => {
                  optionsMap.set(fileName, { replaceDocumentId: existingId, autoRename: false })
                })
              }
              setPnidDuplicateOptions(optionsMap)

              // FS-499: for a multi-file region-selection batch, pre-register
              // every file server-side BEFORE opening the region modal. The
              // returned docIds back the local temp rows; the catalog now
              // shows the full queue from a real DB row instead of React
              // state, so a page refresh leaves the pending rows intact.
              // Multi-page mode skips this — uploadMultiPageFiles pre-registers
              // later with multi_page_set_id properties (would double-create).
              const docIdByName = new Map<string, string>()
              if (!isMultiPage && fileArray.length > 1) {
                const batchId =
                  typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
                try {
                  const resp = await apiService.registerPendingFiles(
                    batchId,
                    fileArray.map(f => {
                      const replaceId = duplicateOptions?.duplicateMap?.get(f.name)
                      return {
                        filename: f.name,
                        fileType,
                        fileSize: f.size,
                        ...(replaceId
                          ? { replaceDocumentId: replaceId }
                          : { autoRename: duplicateOptions?.autoRename ?? true }),
                      }
                    }),
                  )
                  // Key the map by the SENT filename (not the resolved one),
                  // so subsequent lookups in the modal flow find the docId
                  // even when the server renamed `foo.pdf` → `foo (1).pdf`.
                  resp.files.forEach((r, i) => {
                    const sent = fileArray[i]?.name
                    if (sent) docIdByName.set(sent, r.docId)
                  })
                  pendingBatchRef.current = { batchId, docIdByName }
                } catch (err) {
                  console.warn('[FS-499] register-pending failed for region-selection batch; falling back', err)
                  pendingBatchRef.current = null
                }
                // Create local temp catalog rows regardless of whether
                // register-pending succeeded. On success, docIdByName
                // supplies the server-issued docId so the temp row
                // mirrors the real DB row. On failure (API down,
                // network blip), docId is undefined and the legacy
                // local-only path runs — same UX as today's main, no
                // regression. Mirrors the bypass path's behaviour above.
                fileArray.forEach(f => {
                  const docId = docIdByName.get(f.name)
                  uploadCallbacks?.onStart?.(f.name, fileType, { fileSize: f.size, docId })
                })
              } else {
                pendingBatchRef.current = null
              }

              if (fileArray.length > 1) {
                setPnidQueue(fileArray.slice(1))
                setIsBatchUpload(true)
              } else {
                setIsBatchUpload(false)
              }
              setCurrentPnidFileType(fileType)

              // In multi-page mode, initialize collected files array
              if (isMultiPage) {
                setCollectedFiles([])
              }

              // Include per-file duplicate options in modal data for first file
              const firstFileOpts = optionsMap.get(fileArray[0].name) || {}
              const firstDocId = docIdByName.get(fileArray[0].name)
              setModalData(MODAL_TYPE.regionSelection, {
                file: fileArray[0],
                type: fileType,
                name: fileArray[0].name,
                description: '',
                autoRename: true,
                ...firstFileOpts,
                ...(firstDocId && { docId: firstDocId }),
              })
              openModal(MODAL_TYPE.regionSelection)
            }}
          />

      {/* Region selection modal */}
      <RegionSelectionModal
        isOpen={isRegionSelectionOpen}
        onClose={() => {
          // FS-499: discard the leftover pending rows on the server. The
          // file currently in the modal plus any still in pnidQueue have
          // pre-registered DB rows that need cleanup, otherwise they sit
          // as zombie 'pending' status forever (until the BullMQ sweep
          // runs). The local temp rows get cleared via onDiscard so the
          // catalog reflects the discard immediately.
          const pendingBatch = pendingBatchRef.current
          if (pendingBatch) {
            const currentFile = (regionDoc as { file?: File } | null)?.file
            const docIdsToDiscard: string[] = []
            if (currentFile) {
              const id = pendingBatch.docIdByName.get(currentFile.name)
              if (id) docIdsToDiscard.push(id)
            }
            pnidQueue.forEach(f => {
              const id = pendingBatch.docIdByName.get(f.name)
              if (id) docIdsToDiscard.push(id)
            })
            docIdsToDiscard.forEach(id => uploadCallbacks?.onDiscard?.(id))
            if (docIdsToDiscard.length > 0) {
              apiService
                .discardPendingFiles(pendingBatch.batchId, docIdsToDiscard)
                .catch(err => console.error('[FS-499] discard-pending failed', err))
            }
            pendingBatchRef.current = null
          }

          setPnidQueue([])  // Clear queue when closing region selection
          setIsBatchUpload(false)
          setPnidDuplicateOptions(new Map())

          // Cancel already-uploaded files from multi-page batch
          if (activeBatchIdRef.current) {
            const batchId = activeBatchIdRef.current
            activeBatchIdRef.current = null
            apiService.cancelBatch(batchId).catch((err) => {
              console.error('Failed to cancel batch upload:', err)
            })
          }

          closeModal()
        }}
        documentData={regionDoc}
        cropStage={isPfd ? 'diagram' : null}
        onSkip={isBatchUpload ? () => {
          // FS-499: Skip means "don't upload this file, advance to next".
          // Discard the skipped file's pending row server-side and clear
          // the local temp row, otherwise it sits forever in 'pending'
          // status and the catalog shows a zombie row.
          const skippedFile = (regionDoc as { file?: File } | null)?.file
          const pendingBatch = pendingBatchRef.current
          if (skippedFile && pendingBatch) {
            const skippedDocId = pendingBatch.docIdByName.get(skippedFile.name)
            if (skippedDocId) {
              uploadCallbacks?.onDiscard?.(skippedDocId)
              pendingBatch.docIdByName.delete(skippedFile.name)
              apiService
                .discardPendingFiles(pendingBatch.batchId, [skippedDocId])
                .catch(err => console.error('[FS-499] discard-pending (skip) failed', err))
            }
          }
          if (pnidQueue.length > 0) {
            const nextFile = pnidQueue[0]
            const remainingFiles = pnidQueue.slice(1)
            setPnidQueue(remainingFiles)

            const nextFileOpts = pnidDuplicateOptions.get(nextFile.name) || {}
            const nextDocId = pendingBatchRef.current?.docIdByName.get(nextFile.name)
            setModalData(MODAL_TYPE.regionSelection, {
              file: nextFile,
              type: currentPnidFileType,
              name: nextFile.name,
              description: '',
              autoRename: true,
              ...nextFileOpts,
              ...(nextDocId && { docId: nextDocId }),
            })
            openModal(MODAL_TYPE.regionSelection)
          } else if (isMultiPageMode && collectedFiles.length > 0) {
            // All files processed, upload directly
            setIsBatchUpload(false)
            closeModal()
            uploadMultiPageFiles(collectedFiles).catch((err) => {
              console.error('Multi-page upload failed:', err)
              fetchDocuments()
              setCollectedFiles([])
              setIsMultiPageMode(false)
              setIsBatchUpload(false)
              setPnidQueue([])
              setPnidDuplicateOptions(new Map())
            })
          } else {
            // Last file in the batch was skipped — the pendingBatchRef
            // is now empty (onSkip's discard already removed the last
            // entry above). Clear it explicitly so a future change to
            // onClose that iterates the whole map can't double-discard
            // already-processed rows.
            pendingBatchRef.current = null
            setPnidDuplicateOptions(new Map())
            setIsBatchUpload(false)
            closeModal()
          }
        } : undefined}
        onSubmit={async (data) => {
          if (isMultiPageMode) {
            // Multi-page mode: collect region selection without uploading
            const fileWithRegions: FileWithRegions = {
              file: data.file!,
              bboxes: data.bboxes || null,
              name: data.name,
              description: data.description,
              type: data.type as 'pnid' | 'pfd',
              autoRename: data.autoRename,
              replaceDocumentId: data.replaceDocumentId,
            }

            const newCollectedFiles = [...collectedFiles, fileWithRegions]

            // Check if there are more files in queue
            if (pnidQueue.length > 0) {
              // Process next file in queue
              const nextFile = pnidQueue[0]
              const remainingFiles = pnidQueue.slice(1)
              setPnidQueue(remainingFiles)
              setCollectedFiles(newCollectedFiles)

              // Include per-file duplicate options for this file
              const nextFileOpts = pnidDuplicateOptions.get(nextFile.name) || {}
              const nextDocId = pendingBatchRef.current?.docIdByName.get(nextFile.name)
              setModalData(MODAL_TYPE.regionSelection, {
                file: nextFile,
                type: currentPnidFileType,
                name: nextFile.name,
                description: '',
                autoRename: true,
                ...nextFileOpts,
                ...(nextDocId && { docId: nextDocId }),
              })
              // Modal is already region selection, just update the data
              // Force re-render by closing and reopening
              openModal(MODAL_TYPE.regionSelection)
            } else {
              // All files collected, upload directly
              setIsBatchUpload(false)
              closeModal()
              uploadMultiPageFiles(newCollectedFiles).catch((err) => {
                console.error('Multi-page upload failed:', err)
                fetchDocuments()
                setCollectedFiles([])
                setIsMultiPageMode(false)
                setIsBatchUpload(false)
                setPnidQueue([])
                setPnidDuplicateOptions(new Map())
              })
            }
          } else {
            // Normal mode: upload immediately
            await onModalSubmit(MODAL_TYPE.regionSelection, data)
            // After region selection, check if there are more files in queue
            fetchDocuments()
            if (pnidQueue.length > 0) {
                // Process next file in queue
                const nextFile = pnidQueue[0]
                const remainingFiles = pnidQueue.slice(1)
                setPnidQueue(remainingFiles)

                // Include per-file duplicate options for this file
                const nextFileOpts = pnidDuplicateOptions.get(nextFile.name) || {}
                const nextDocId = pendingBatchRef.current?.docIdByName.get(nextFile.name)
                setModalData(MODAL_TYPE.regionSelection, {
                  file: nextFile,
                  type: currentPnidFileType,
                  name: nextFile.name,
                  description: '',
                  autoRename: true,
                  ...nextFileOpts,
                  ...(nextDocId && { docId: nextDocId }),
                })
                // Modal is already region selection, just update the data
                // Force re-render by closing and reopening
                openModal(MODAL_TYPE.regionSelection)
              } else {
                // No more files, close the upload modal (files have been uploaded)
                setIsBatchUpload(false)
                setPnidDuplicateOptions(new Map())
                pendingBatchRef.current = null
                closeModal()
              }
          }
        }}
      />

      {/* Call config modal */}
      <CallConfigModal
        isOpen={isCallConfigOpen}
        onClose={closeModal}
      />
    </>
  )
}

export default ModalProvider
